import {
  Component,
  ViewContainerRef,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { CDN_URL } from '@maximizer/core/shared/domain';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import { TeamMemberService } from '@maximizer/outlook/shared/data-access';
import {
  EmailPosition,
  EntryType,
  OutlookEmail,
} from '@maximizer/outlook/shared/domain';
import {
  OutlookAvatarClassesPipe,
  OutlookAvatarLettersPipe,
  TranslateVerticalPipe,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_DROPDOWNBUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_CHECKBOX } from '@progress/kendo-angular-inputs';
import {
  EntryMenuItem,
  EntryMovePositionEvent,
  EntryOutlookEmail,
  EntrySelectionEvent,
} from '../compose.model';

@Component({
  selector: 'maximizer-outlook-entry-list-item',
  templateUrl: './entry-list-item.component.html',
  imports: [
    KENDO_DROPDOWNBUTTON,
    KENDO_CHECKBOX,
    OutlookAvatarClassesPipe,
    OutlookAvatarLettersPipe,
    TranslatePipe,
    TranslateVerticalPipe,
    UI_PIPES,
  ],
})
export class OutlookEntryListItemComponent {
  cdn = inject(CDN_URL);
  translate = inject(TranslateService);
  teamMemberService = inject(TeamMemberService);
  viewContainerRef = inject(ViewContainerRef);

  entryItem = input<EntryOutlookEmail>();
  userEmails = input<Set<string>>(new Set());

  position = output<EntryMovePositionEvent>();
  selection = output<EntrySelectionEvent>();
  openEntry = output<OutlookEmail>();

  EntryType = EntryType;

  entryActionItems = computed<EntryMenuItem[]>(() => [
    {
      id: 'to',
      text: this.translate.instant('outlook.compose.entryList.move.to'),
      click: () => this.onEntryPositionChange('to'),
      disabled: this.entryItem()?.position === 'to',
    },
    {
      id: 'cc',
      text: this.translate.instant('outlook.compose.entryList.move.cc'),
      click: () => this.onEntryPositionChange('cc'),
      disabled: this.entryItem()?.position === 'cc',
    },
    {
      id: 'bcc',
      text: this.translate.instant('outlook.compose.entryList.move.bcc'),
      click: () => this.onEntryPositionChange('bcc'),
      disabled: this.entryItem()?.position === 'bcc',
    },
    {
      id: 'saveOnly',
      text: this.translate.instant('outlook.compose.entryList.move.save'),
      click: () => this.onEntryPositionChange('saveOnly'),
      disabled: this.entryItem()?.position === 'saveOnly',
    },
    {
      id: 'remove',
      text: this.translate.instant('outlook.compose.entryList.move.remove'),
      click: () => this.onEntryPositionChange('remove'),
    },
  ]);

  onEntryPositionChange(action: EmailPosition): void {
    const entry = this.entryItem();
    let oldPosition = entry?.position ?? `to`;

    if (!entry?.id) {
      console.error('Invalid entry item or missing ID');
      return;
    }

    this.position.emit({ entry, oldPosition, newPosition: action });
  }

  onSelectionChange(selected: boolean): void {
    const entry = this.entryItem();

    if (entry) {
      this.selection.emit({
        entry,
        selected,
      });
    }
  }

  openEntryDetails(): void {
    const entry = this.entryItem();

    if (entry) {
      this.openEntry.emit(entry);
    }
  }

  public isUser(email: string): boolean {
    return this.userEmails().has(email.toLowerCase());
  }

  isTeamMember(email: string): boolean {
    return this.teamMemberService.isTeamMember(email);
  }
}
